import React, { useState } from 'react';
import { XIcon, MessageSquareIcon, AtSignIcon, SendIcon } from 'lucide-react';
import { Ticket, Status } from '../data/mockData';
interface TicketDetailModalProps {
  ticket: Ticket;
  onClose: () => void;
  onUpdateTicket: (updatedTicket: Ticket) => void;
}
interface Comment {
  id: string;
  author: string;
  text: string;
  timestamp: string;
}
export function TicketDetailModal({
  ticket,
  onClose,
  onUpdateTicket
}: TicketDetailModalProps) {
  const [status, setStatus] = useState<Status>(ticket.status);
  const [commentText, setCommentText] = useState('');
  const [comments, setComments] = useState<Comment[]>([{
    id: 'c1',
    author: ticket.submittedBy,
    text: 'Please look into this as soon as possible. It is impacting my workflow.',
    timestamp: new Date(ticket.createdDate).toLocaleString()
  }]);
  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newStatus = e.target.value as Status;
    setStatus(newStatus);
    onUpdateTicket({
      ...ticket,
      status: newStatus
    });
  };
  const handleTagSubmitter = () => {
    setCommentText(prev => `${prev} @${ticket.submittedBy} `);
  };
  const handlePostComment = () => {
    if (!commentText.trim()) return;
    const newComment: Comment = {
      id: Date.now().toString(),
      author: 'Admin User',
      text: commentText,
      timestamp: new Date().toLocaleString()
    };
    setComments([...comments, newComment]);
    setCommentText('');
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm" data-id="element-117">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200" data-id="element-118">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/50" data-id="element-119">
          <div data-id="element-120">
            <div className="flex items-center space-x-3" data-id="element-121">
              <span className="text-sm font-medium text-blue-600 bg-blue-50 px-2.5 py-0.5 rounded-md border border-blue-100" data-id="element-122">
                {ticket.id}
              </span>
              <h2 className="text-lg font-bold text-gray-900" data-id="element-123">
                {ticket.subject}
              </h2>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-124">
            <XIcon className="w-5 h-5" data-id="element-125" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6" data-id="element-126">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-127">
            {/* Left Column: Details */}
            <div className="md:col-span-1 space-y-6" data-id="element-128">
              <div data-id="element-129">
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3" data-id="element-130">
                  Ticket Details
                </h3>
                <div className="space-y-4 text-sm" data-id="element-131">
                  <div data-id="element-132">
                    <span className="block text-gray-500 mb-1" data-id="element-133">
                      Submitted By
                    </span>
                    <div className="font-medium text-gray-900" data-id="element-134">
                      {ticket.submittedBy}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-135">
                      {ticket.role} • {ticket.department}
                    </div>
                  </div>
                  <div data-id="element-136">
                    <span className="block text-gray-500 mb-1" data-id="element-137">Priority</span>
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium border ${ticket.priority === 'Critical' ? 'bg-red-100 text-red-700 border-red-200' : ticket.priority === 'High' ? 'bg-orange-100 text-orange-700 border-orange-200' : ticket.priority === 'Medium' ? 'bg-yellow-100 text-yellow-700 border-yellow-200' : 'bg-green-100 text-green-700 border-green-200'}`} data-id="element-138">
                      {ticket.priority}
                    </span>
                  </div>
                  <div data-id="element-139">
                    <span className="block text-gray-500 mb-1" data-id="element-140">Status</span>
                    <select value={status} onChange={handleStatusChange} className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white font-medium text-gray-900" data-id="element-141">
                      <option value="Open" data-id="element-142">Open</option>
                      <option value="In Progress" data-id="element-143">In Progress</option>
                      <option value="Waiting on User" data-id="element-144">Waiting on User</option>
                      <option value="Resolved" data-id="element-145">Resolved</option>
                      <option value="Closed" data-id="element-146">Closed</option>
                      <option value="Overdue" data-id="element-147">Overdue</option>
                    </select>
                  </div>
                  <div data-id="element-148">
                    <span className="block text-gray-500 mb-1" data-id="element-149">
                      Assigned To
                    </span>
                    <div className="font-medium text-gray-900" data-id="element-150">
                      {ticket.assignedTo}
                    </div>
                  </div>
                  <div data-id="element-151">
                    <span className="block text-gray-500 mb-1" data-id="element-152">Created</span>
                    <div className="text-gray-900" data-id="element-153">
                      {new Date(ticket.createdDate).toLocaleString()}
                    </div>
                  </div>
                  <div data-id="element-154">
                    <span className="block text-gray-500 mb-1" data-id="element-155">Category</span>
                    <div className="text-gray-900" data-id="element-156">{ticket.category}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Activity & Comments */}
            <div className="md:col-span-2 flex flex-col h-full" data-id="element-157">
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3 flex items-center" data-id="element-158">
                <MessageSquareIcon className="w-4 h-4 mr-2" data-id="element-159" />
                Activity & Comments
              </h3>

              {/* Comments List */}
              <div className="flex-1 space-y-4 mb-6" data-id="element-160">
                {comments.map(comment => <div key={comment.id} className="bg-gray-50 rounded-lg p-4 border border-gray-100" data-id="element-161">
                    <div className="flex justify-between items-start mb-2" data-id="element-162">
                      <span className="font-medium text-sm text-gray-900" data-id="element-163">
                        {comment.author}
                      </span>
                      <span className="text-xs text-gray-500" data-id="element-164">
                        {comment.timestamp}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap" data-id="element-165">
                      {comment.text}
                    </p>
                  </div>)}
              </div>

              {/* Comment Input */}
              <div className="mt-auto border border-gray-200 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent transition-shadow" data-id="element-166">
                <textarea value={commentText} onChange={e => setCommentText(e.target.value)} placeholder="Type your comment here..." className="w-full p-4 text-sm border-none focus:ring-0 resize-none min-h-[100px]" data-id="element-167" />
                <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 flex items-center justify-between" data-id="element-168">
                  <button onClick={handleTagSubmitter} className="flex items-center space-x-1 text-xs font-medium text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-2.5 py-1.5 rounded-md transition-colors" data-id="element-169">
                    <AtSignIcon className="w-3.5 h-3.5" data-id="element-170" />
                    <span data-id="element-171">Tag Submitter</span>
                  </button>
                  <button onClick={handlePostComment} disabled={!commentText.trim()} className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors" data-id="element-172">
                    <span data-id="element-173">Post</span>
                    <SendIcon className="w-4 h-4" data-id="element-174" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}